import React from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Spinner,
  UncontrolledTooltip,
  TabContent,
  TabPane,
} from 'reactstrap';
import { read as xlsxRead, utils as xlsxUtils } from 'xlsx';
import axiosPrivate from 'config/axiosPrivate';
import { useDispatch } from 'react-redux';
import { transformToSemanticGap } from 'helpers/FormatKeywordsHelper';
import GapGenerationModal from 'components/shared/modal/GapGenerationModal';
import { fetchAdminClientTextOrderDisplay as fetchAdminTextOrderDisplay } from 'store/AdminClient/OrderDisplay/features';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import './RedactionModal.css';
import '../SmartcontentModal/SmartContentPopin.css';

const parseGapWords = (semanticGap) => {
  if (!semanticGap || !semanticGap.trim()) return [];
  const gap = transformToSemanticGap(semanticGap);
  return Array.isArray(gap)
    ? gap
        .map((g) => {
          if (typeof g === 'string') return g;
          const word = (g?.word || '').toString().trim();
          const freq = Number(g?.frequency ?? 1);
          return word ? `${word}(${Number.isNaN(freq) ? 1 : freq})` : '';
        })
        .map((s) => (s || '').trim())
        .filter(Boolean)
    : [];
};

const parseListField = (value) =>
  String(value || '')
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

const YoutubeRedactionModal = ({
  isOpen,
  onClose,
  orderId,
  title,
  aiAgentId,
  orderWebsite,
  language,
  country,
  orderTextAmount,
  t = (key) => key,
  generationContext = 'AC',
}) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = React.useState({
    keyword: '',
    url: '',
    semanticGap: '',
    internalLinking: '',
    originalText: '',
    retranscription: '',
  });
  const [errors, setErrors] = React.useState({});
  const [isLoading, setIsLoading] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertType, setAlertType] = React.useState('danger');
  const [isGapModalOpen, setIsGapModalOpen] = React.useState(false);
  const [gapMode, setGapMode] = React.useState('aqisis');
  const [gapAnalysisEnabled, setGapAnalysisEnabled] = React.useState(null);
  const [remainingCredits, setRemainingCredits] = React.useState(null);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = React.useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = React.useState(false);
  const [isContentLimitModalOpen, setIsContentLimitModalOpen] = React.useState(false);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('tab1');
  const [filePreviewData, setFilePreviewData] = React.useState([]);
  const [currentGeneratedTextsCount, setCurrentGeneratedTextsCount] = React.useState(0);
  const [isDragging, setIsDragging] = React.useState(false);
  const [isSeoSectionOpen, setIsSeoSectionOpen] = React.useState(true);
  const [pollingError, setPollingError] = React.useState(null);
  const pollingIntervalRef = React.useRef(null);

  const normalizedGenerationContext = (generationContext || 'AC').toUpperCase();
  const generationStorageKey = orderId
    ? `${normalizedGenerationContext}_Generation_${orderId}`
    : null;
  const generationEventName = `${normalizedGenerationContext.toLowerCase()}-generation-changed`;

  const normalizeUrl = (input) =>
    String(input || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');

  const checkWebsiteQuota = async () => {
    const normalizedOrderWebsite = normalizeUrl(orderWebsite);
    if (!normalizedOrderWebsite) return true;
    try {
      const websitesResponse = await axiosPrivate.get('/project/website/all');
      const websites = Array.isArray(websitesResponse?.data) ? websitesResponse.data : [];
      const matchingWebsite = websites.find((websiteItem) => {
        const websiteUrl = normalizeUrl(
          websiteItem?.urlSite || websiteItem?.url || websiteItem?.name || ''
        );
        return websiteUrl === normalizedOrderWebsite;
      });
      if (!matchingWebsite?.id) return true;
      const quotasResponse = await axiosPrivate.get(`/project/website-quotas/${matchingWebsite.id}`);
      const maxTexts = Number(quotasResponse?.data?.maxTexts ?? 0);
      const usedTexts = Number(quotasResponse?.data?.usedTexts ?? 0);
      return maxTexts - usedTexts > 0;
    } catch {
      return true;
    }
  };

  const setGenerationStatus = (isGenerating) => {
    if (!generationStorageKey) return;
    try {
      window.localStorage.setItem(generationStorageKey, isGenerating ? '1' : '0');
      window.dispatchEvent(new Event(generationEventName));
    } catch {
      // ignore
    }
  };

  const checkJobStatus = async () => {
    if (!orderId) return;
    try {
      const response = await axiosPrivate.get(`/project/job-status/${orderId}`);
      if (response.status === 204) {
        try {
          if (window.localStorage.getItem('AdminClient_ProjectDisplay_id') == orderId) {
            await dispatch(fetchAdminTextOrderDisplay(orderId));
          }
          setPollingError(null);
        } catch {
          setPollingError(t('Erreur lors de la récupération des textes'));
        } finally {
          stopPolling();
        }
      }
    } catch {
      setPollingError(t('Erreur lors de la surveillance des jobs'));
    }
  };

  const startPolling = () => {
    if (pollingIntervalRef.current || !orderId) return;
    checkJobStatus();
    setGenerationStatus(true);
    pollingIntervalRef.current = setInterval(checkJobStatus, 10000);
  };

  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setGenerationStatus(false);
  };

  React.useEffect(() => {
    return () => stopPolling();
  }, []);

  React.useEffect(() => {
    if (!isOpen) return;
    resetForm();
    setIsSeoSectionOpen(true);
    fetchCurrentTextsCount();
    fetchCredits();
  }, [isOpen, orderId]);

  const fetchCredits = async () => {
    try {
      const { data } = await axiosPrivate.get('/project/company-quotas');
      setGapAnalysisEnabled(data?.gapAnalysisEnabled === true);
      const totalCredits = Number(data?.totalCredits ?? 0);
      const usedCredits = Number(data?.usedCredits ?? 0);
      if (!Number.isNaN(totalCredits) && !Number.isNaN(usedCredits)) {
        setRemainingCredits(Math.max(totalCredits - usedCredits, 0));
      } else {
        setRemainingCredits(null);
      }
    } catch {
      setGapAnalysisEnabled(false);
      setRemainingCredits(null);
    }
  };

  const fetchCurrentTextsCount = async () => {
    if (!orderId || !orderTextAmount) return;
    try {
      const response = await axiosPrivate.get(`/texts/external/by-project/${orderId}`);
      if (response.status === 200) {
        setCurrentGeneratedTextsCount((response.data || []).length);
      }
    } catch {
      // ignore
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/modele-youtube.xlsx';
    link.download = 'modele-youtube.xlsx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const processExcelFile = (file) => {
    const allowedTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv',
    ];
    if (
      !allowedTypes.includes(file.type) &&
      !file.name.endsWith('.csv') &&
      !file.name.endsWith('.xlsx') &&
      !file.name.endsWith('.xls')
    ) {
      setAlertMessage(
        t('Format de fichier non supporté. Veuillez sélectionner un fichier .xlsx, .xls ou .csv')
      );
      setAlertType('danger');
      return;
    }

    setSelectedFile(file);
    setAlertMessage('');
    setErrors({});

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = xlsxRead(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = xlsxUtils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length > 1) {
          const rows = jsonData.slice(1).filter((row) => row.length > 0 && row[0]);
          if (orderTextAmount && rows.length > 0) {
            const remainingTexts = orderTextAmount - currentGeneratedTextsCount;
            if (rows.length > remainingTexts) {
              setErrors({
                file: t(
                  `Actuellement, le fichier comprend ${rows.length} textes ; il reste cependant ${remainingTexts} texte(s) à générer.`
                ),
              });
              setAlertMessage(
                t(
                  `Vous avez atteint la limite de textes pour ce projet. Il ne reste que ${remainingTexts} texte(s) à générer.`
                )
              );
              setAlertType('danger');
              setSelectedFile(null);
              setFilePreviewData([]);
              return;
            }
          }
          setFilePreviewData(
            rows.map((row) => ({
              keyword: row[0] || '',
              url: row[1] || '',
              gap: row[2] || '',
              maillageInterne: row[3] || '',
              retranscription: row[4] || '',
              originalText: row[5] || '',
            }))
          );
        }
      } catch {
        setAlertMessage(t('Erreur lors de la lecture du fichier'));
        setAlertType('danger');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) processExcelFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processExcelFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFilePreviewData([]);
    setErrors({});
    setAlertMessage('');
    const fileInput = document.getElementById('youtubeBatchUpload');
    if (fileInput) fileInput.value = '';
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (alertMessage) setAlertMessage('');
  };

  const validateForm = () => {
    const newErrors = {};
    if (activeTab === 'tab1') {
      if (!formData.keyword.trim()) newErrors.keyword = t('Le mot clé est obligatoire');
    } else if (!selectedFile) {
      newErrors.file = t('Veuillez sélectionner un fichier Excel');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    if (activeTab === 'tab1') return !!formData.keyword.trim();
    return selectedFile !== null;
  };

  const buildSinglePayload = () => ({
    agentId: aiAgentId || '',
    keyword: formData.keyword.trim(),
    originalText: formData.originalText.trim() || undefined,
    url: formData.url.trim() || undefined,
    retranscription: formData.retranscription.trim() || undefined,
    gap: parseGapWords(formData.semanticGap),
    maillageInterne: parseListField(formData.internalLinking),
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const canCreateContent = await checkWebsiteQuota();
    if (!canCreateContent) {
      handleModalClose();
      setIsContentLimitModalOpen(true);
      return;
    }

    if (remainingCredits !== null && remainingCredits <= 0) {
      handleModalClose();
      setIsCreditsLimitModalOpen(true);
      return;
    }

    if (!validateForm() || !orderId) {
      setAlertMessage(t('Veuillez corriger les erreurs dans le formulaire'));
      setAlertType('danger');
      return;
    }

    setIsLoading(true);
    setAlertMessage('');

    try {
      if (activeTab === 'tab1') {
        await axiosPrivate.post(
          `/project/generation/youtube-description-text/${orderId}`,
          buildSinglePayload()
        );
      } else {
        if (orderTextAmount && filePreviewData.length > 0) {
          const remainingTexts = orderTextAmount - currentGeneratedTextsCount;
          if (filePreviewData.length > remainingTexts) {
            setAlertMessage(
              t(
                `Vous avez atteint la limite de textes pour ce projet. Il ne reste que ${remainingTexts} texte(s) à générer.`
              )
            );
            setAlertType('danger');
            setIsLoading(false);
            return;
          }
        }

        const formDataToSend = new FormData();
        formDataToSend.append('excelFile', selectedFile);

        await axiosPrivate.post(
          `/project/generation/youtube-description-batch-text/${orderId}`,
          formDataToSend,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
      }

      setAlertMessage(t('Génération en cours... Surveillance des jobs.'));
      setAlertType('info');
      startPolling();
      resetForm();
      onClose();
    } catch (error) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t('Erreur lors de la soumission');
      setAlertMessage(errorMsg);
      setAlertType('danger');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      keyword: '',
      url: '',
      semanticGap: '',
      internalLinking: '',
      originalText: '',
      retranscription: '',
    });
    setErrors({});
    setAlertMessage('');
    setSelectedFile(null);
    setFilePreviewData([]);
    setActiveTab('tab1');
    const fileInput = document.getElementById('youtubeBatchUpload');
    if (fileInput) fileInput.value = '';
  };

  const handleModalClose = () => {
    if (isLoading) return;
    resetForm();
    onClose();
  };

  const handleSemanticGapChangeFromModal = (event) => {
    const value = event?.target?.value || '';
    setFormData((prev) => ({ ...prev, semanticGap: value }));
  };

  const hasTargetUrl = !!(formData.url || '').trim();

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleModalClose}
        size="lg"
        className="redaction-modal youtube-redaction-modal"
        backdrop={isLoading ? 'static' : true}
        keyboard={!isLoading}
      >
        <ModalHeader toggle={!isLoading ? handleModalClose : undefined}>
          {title || t('Créer un nouveau contenu')}
        </ModalHeader>
        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {alertMessage && (
              <Alert color={alertType} className="mb-3">
                {alertMessage}
              </Alert>
            )}
            {pollingError && (
              <Alert color="warning" className="mb-3">
                {pollingError}
              </Alert>
            )}

            <div className="modal-option-row mb-3">
              <button
                type="button"
                className={`option-card ${activeTab === 'tab1' ? 'active' : ''}`}
                onClick={() => {
                  setActiveTab('tab1');
                  handleRemoveFile();
                }}
              >
                <img
                  src={activeTab === 'tab1' ? 'Images/one-text-acti.png' : 'Images/one-text-desact.png'}
                  alt="One text icon"
                  style={{ width: '40px', height: '40px', marginRight: '12px' }}
                />
                <div className="option-text" style={{ marginLeft: '-50px' }}>
                  <div className="option-title">{t('Rédiger une description YouTube')}</div>
                  <div className="option-subtitle">
                    {t('Créez une description pour un mot-clé spécifique')}
                  </div>
                </div>
                <div className={`option-check ${activeTab === 'tab1' ? 'checked' : ''}`} />
              </button>

              <button
                type="button"
                className={`option-card ${activeTab === 'tab2' ? 'active' : ''}`}
                onClick={() => setActiveTab('tab2')}
              >
                <img
                  src={activeTab === 'tab2' ? 'Images/act-batch-text.png' : 'Images/batch-icon-desact.png'}
                  alt="Batch icon"
                  style={{ width: '40px', height: '40px', marginRight: '-70px' }}
                />
                <div className="option-text" style={{ marginLeft: '70px' }}>
                  <div className="option-title">{t('Rédiger un lot de descriptions')}</div>
                  <div className="option-subtitle">
                    {t('Générez plusieurs descriptions en important un fichier Excel')}
                  </div>
                </div>
                <div className={`option-check ${activeTab === 'tab2' ? 'checked' : ''}`} />
              </button>
            </div>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="tab1">
                <div className="smart-content-popin-section">
                  <h6 className="smart-content-popin-section-title">
                    <img src="/Images/info-glob.png" alt="info" className="smart-content-popin-info-icon" />
                    {t('Section 1 – Informations requises (obligatoire)')}
                  </h6>
                  <FormGroup>
                    <Label for="youtubeKeyword" className="smart-content-popin-label required-field">
                      {t('Mot-clé')}
                    </Label>
                    <Input
                      type="text"
                      id="youtubeKeyword"
                      name="keyword"
                      value={formData.keyword}
                      onChange={handleInputChange}
                      placeholder={t('Ex: télépéage automatique')}
                      disabled={isLoading}
                      invalid={!!errors.keyword}
                      className="smart-content-popin-input keyword-input"
                    />
                    {errors.keyword && <div className="invalid-feedback d-block">{errors.keyword}</div>}
                  </FormGroup>
                </div>

                <div className="smart-content-popin-section">
                  <h6
                    className="smart-content-popin-section-title-seo"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                    }}
                    onClick={() => setIsSeoSectionOpen((prev) => !prev)}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <img src="/Images/seo-settings.png" alt="info" className="smart-content-popin-info-icon" />
                      {t('Section 2 – Paramètres SEO avancés (optionnel)')}
                    </span>
                    <span
                      style={{
                        display: 'inline-block',
                        transition: 'transform 0.2s ease',
                        transform: isSeoSectionOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        fontSize: 14,
                        color: '#818181',
                      }}
                    >
                      ▼
                    </span>
                  </h6>

                  {isSeoSectionOpen && (
                    <>
                      <FormGroup>
                        <Label for="youtubeUrl" className="smart-content-popin-label">
                          {t('URL')}
                        </Label>
                        <Input
                          type="url"
                          id="youtubeUrl"
                          name="url"
                          value={formData.url}
                          onChange={handleInputChange}
                          placeholder={t('Ex: https://www.youtube.com/watch?v=...')}
                          disabled={isLoading}
                          className="smart-content-popin-input smart-content-popin-url-input"
                        />
                      </FormGroup>

                      {hasTargetUrl && (
                        <FormGroup>
                          <Label for="youtubeOriginalText" className="smart-content-popin-label">
                            {t('Description existante')}
                          </Label>
                          <Input
                            type="textarea"
                            id="youtubeOriginalText"
                            name="originalText"
                            value={formData.originalText}
                            onChange={handleInputChange}
                            placeholder={t('Collez la description existante de la vidéo.')}
                            disabled={isLoading}
                            rows="5"
                            className="smart-content-popin-input semantic-gap-textarea"
                          />
                        </FormGroup>
                      )}

                      <FormGroup>
                        <Label for="youtubeRetranscription" className="smart-content-popin-label">
                          {t('Retranscription')}
                        </Label>
                        <Input
                          type="textarea"
                          id="youtubeRetranscription"
                          name="retranscription"
                          value={formData.retranscription}
                          onChange={handleInputChange}
                          placeholder={t('Collez la retranscription de la vidéo.')}
                          disabled={isLoading}
                          rows="5"
                          className="smart-content-popin-input semantic-gap-textarea"
                        />
                      </FormGroup>

                      <FormGroup>
                        <Label for="youtubeSemanticGap" className="smart-content-popin-label">
                          {t('Gap sémantique')}
                          <span id="gapTooltipYoutube" className="ms-2" style={{ cursor: 'pointer', color: '#ef4444' }}>
                            ⓘ
                          </span>
                        </Label>
                        <Input
                          type="textarea"
                          id="youtubeSemanticGap"
                          name="semanticGap"
                          value={formData.semanticGap}
                          onChange={handleInputChange}
                          placeholder={t('keyword1 (2),\nkeyword2 (1),\nkeyword3 (1),\nkeyword4 (2),\n.....')}
                          disabled={isLoading}
                          rows="4"
                          className="semantic-gap-textarea smart-content-popin-input"
                        />
                        <UncontrolledTooltip placement="right" target="gapTooltipYoutube" className="custom-redac-tooltip">
                          {t(
                            'Saisissez chaque mot-clé suivi de sa fréquence entre parenthèses, séparés par une virgule ou un retour à la ligne. \n Exemple :\n chaussures(3), robe(2), pantalon(1)'
                          )}
                        </UncontrolledTooltip>
                        <div className="semantic-gap-actions smart-content-popin-gap-actions">
                          <button
                            type="button"
                            className="gap-light-btn"
                            onClick={() => {
                              if (gapAnalysisEnabled !== true) return;
                              setIsGapModalOpen(true);
                            }}
                            disabled={gapAnalysisEnabled !== true || isLoading}
                          >
                            <span className="gap-refresh-icon">
                              <img
                                src="/Images/gap-icon-gene.png"
                                alt={t('Générer une couverture sémantique')}
                                style={{ width: 16, height: 16, marginLeft: -6 }}
                              />
                            </span>
                            <span className="gap-refresh-label">
                              {t('Générer une couverture sémantique')}
                            </span>
                          </button>
                        </div>
                      </FormGroup>

                      <FormGroup>
                        <Label for="youtubeInternalLinking" className="smart-content-popin-label">
                          {t('Maillage interne')}
                        </Label>
                        <Input
                          type="textarea"
                          id="youtubeInternalLinking"
                          name="internalLinking"
                          value={formData.internalLinking}
                          onChange={handleInputChange}
                          placeholder={t('URLs des pages à lier (séparées par des virgules)')}
                          disabled={isLoading}
                          rows="3"
                          className="smart-content-popin-input"
                        />
                      </FormGroup>
                    </>
                  )}
                </div>
              </TabPane>

              <TabPane tabId="tab2">
                <div className="batch-upload-container">
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '20px' }}>
                    <img
                      src="Images/icon-excel-import.png"
                      alt="excel"
                      style={{ width: '40px', height: '40px', marginTop: '20px' }}
                    />
                    <p style={{ fontSize: '14px', marginTop: '30px' }}>
                      {t("Besoin d'aide pour préparer votre fichier ?")}
                    </p>
                  </div>

                  <div className="d-flex align-items-center mb-3" style={{ gap: '12px' }}>
                    <input
                      id="youtubeBatchUpload"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      style={{
                        backgroundColor: '#FDFDFC',
                        color: '#2F979E',
                        border: '2px solid #2F979E',
                        height: '40px',
                        width: '250px',
                        borderRadius: '8px',
                        marginLeft: '55px',
                      }}
                    >
                      {t('Télécharger le modèle (Excel)')}
                    </button>
                  </div>

                  {errors.file && (
                    <div className="text-danger mt-2" style={{ fontSize: '0.875rem' }}>
                      {errors.file}
                    </div>
                  )}
                </div>

                {!selectedFile ? (
                  <div
                    className={`batch1-upload-container ${isDragging ? 'dragging' : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    style={{
                      border: isDragging ? '2px dashed #2F979E' : '2px dashed #E0E0E0',
                      backgroundColor: isDragging ? '#f0f9fa' : '#FAFAFA',
                      borderRadius: '12px',
                      padding: '40px',
                      textAlign: 'center',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <img
                      src="Images/batch-icon-desact.png"
                      alt="Upload icon"
                      style={{ width: '60px', height: '60px', marginBottom: '20px' }}
                    />
                    <p style={{ fontSize: '16px', fontWeight: '600', marginBottom: '10px' }}>
                      {isDragging ? t('Déposez votre fichier ici') : t('Glissez-déposez votre fichier Excel')}
                    </p>
                    <p style={{ fontSize: '14px', color: '#616161', marginBottom: '20px' }}>
                      {t('ou')}
                    </p>
                    <label htmlFor="youtubeBatchUpload">
                      <span
                        style={{
                          display: 'inline-block',
                          backgroundColor: '#2F979E',
                          color: '#fff',
                          padding: '10px 24px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: 500,
                        }}
                      >
                        {t('Parcourir les fichiers')}
                      </span>
                    </label>
                    <p style={{ fontSize: '12px', color: '#616161', marginTop: '15px' }}>
                      {t('Formats acceptés : .xlsx, .xls (max 10 Mo)')}
                    </p>
                  </div>
                ) : (
                  <div
                    className="file-selected-container"
                    style={{
                      border: '1px solid #E0E0E0',
                      borderRadius: '12px',
                      padding: '20px',
                      backgroundColor: '#FAFAFA',
                      marginTop: '20px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <img
                          src="Images/icon-succ.png"
                          alt="Excel file"
                          style={{ width: '40px', height: '40px' }}
                        />
                        <div>
                          <p style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>{selectedFile.name}</p>
                          <p style={{ fontSize: '13px', fontWeight: '500', color: '#16A34A', margin: '4px 0 0' }}>
                            {filePreviewData?.length || 0} {t('contenus détectés')}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleRemoveFile}
                        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                        title={t('Supprimer le fichier')}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </TabPane>
            </TabContent>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={handleModalClose} disabled={isLoading}>
              {t('Annuler')}
            </Button>
            <Button
              color="primary"
              type="submit"
              disabled={isLoading || !isFormValid()}
              style={{ opacity: !isFormValid() && !isLoading ? 0.6 : 1 }}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {activeTab === 'tab1' ? t('Traitement...') : t('Traitement du fichier...')}
                </>
              ) : (
                t('Générer le contenu')
              )}
            </Button>
          </ModalFooter>
        </Form>

        <GapGenerationModal
          isOpen={isGapModalOpen}
          onClose={() => setIsGapModalOpen(false)}
          mode={gapMode}
          onModeChange={setGapMode}
          keyword={formData.keyword}
          orderWebsite={orderWebsite}
          url={formData.url}
          language={language}
          country={country}
          contentScore={null}
          handleKeywordChange={handleSemanticGapChangeFromModal}
          onUpdate={() => {}}
          disableLightGap={gapAnalysisEnabled !== true}
        />

        <ProjectCreatedModal
          isOpen={isContentLimitModalOpen}
          onClose={() => setIsContentLimitModalOpen(false)}
          title={t('Limite de contenus atteinte')}
          message={t(
            'Vous avez atteint le nombre maximum de contenus inclus dans votre forfait. Passez à une offre supérieure pour continuer à créer et optimiser vos contenus.'
          )}
          confirmButtonText={t('Passer au forfait supérieur')}
          onConfirm={() => {
            setIsContentLimitModalOpen(false);
            if (!window.location.pathname.startsWith('/Consultant')) {
              window.location.assign('/AdminClient/Forfait');
            }
          }}
          secondaryButtonText={t('Fermer')}
          onSecondaryAction={() => setIsContentLimitModalOpen(false)}
        />

        <ProjectCreatedModal
          isOpen={isCreditsLimitModalOpen}
          onClose={() => setIsCreditsLimitModalOpen(false)}
          title={t('Limite atteinte')}
          message={t(
            'Vous avez atteint la limite autorisée par votre forfait.\n\nRechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.'
          )}
          confirmButtonText={t('Acheter des crédits')}
          onConfirm={() => {
            setIsCreditsLimitModalOpen(false);
            setIsBuyCreditsModalOpen(true);
          }}
          secondaryButtonText={t('Passer à une offre supérieure')}
          onSecondaryAction={() => {
            setIsCreditsLimitModalOpen(false);
            if (!window.location.pathname.startsWith('/Consultant')) {
              window.location.assign('/AdminClient/Abonnement');
            }
          }}
        />
        <BuyCreditsModal open={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />
      </Modal>
    </>
  );
};

export default YoutubeRedactionModal;
